import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { HelpCircleIcon, PlusIcon, SearchIcon, XIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
type FaqItem = {
  q: string;
  a: string;
};
type FaqAccordionProps = {
  items: FaqItem[];
  searchable?: boolean;
  idPrefix: string;
};
export function FaqAccordion({
  items,
  searchable = false,
  idPrefix
}: FaqAccordionProps) {
  const {
    t,
    lang
  } = useLanguage();
  const [query, setQuery] = useState('');
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const results = useMemo(() => {
    const term = query.trim().toLowerCase();
    const indexed = items.map((item, index) => ({
      ...item,
      index
    }));
    if (!term) return indexed;
    return indexed.filter(item => item.q.toLowerCase().includes(term) || item.a.toLowerCase().includes(term));
  }, [query, items]);
  return <div data-id="element-318" data-mp="2qdbsnchtyibl">
      {searchable ? <div className="relative mb-6" data-id="element-319" data-mp="2qca5wargc7e1">
          <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" aria-hidden="true" data-id="element-320" data-mp="1fibo86vc4jqh" />
          <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder={t.faq.searchPlaceholder} aria-label={t.faq.searchLabel} className="h-12 w-full rounded-full border border-petal bg-white pl-11 pr-12 text-sm text-ink outline-none transition-colors duration-150 ease-smooth focus:border-mauve focus:ring-2 focus:ring-mauve/20" data-id="element-321" data-mp="1kjr7btdycg68" />
          {query ? <button type="button" onClick={() => setQuery('')} aria-label={t.faq.clearSearch} className="absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full text-ink/50 transition-colors duration-150 ease-smooth hover:bg-petal hover:text-mauve-deep" data-id="element-322" data-mp="nt8hmkfz6xao">
              <XIcon className="h-4 w-4" data-id="element-323" data-mp="3v4qjfybfgttn" />
            </button> : null}
        </div> : null}

      <ul className="divide-y divide-petal overflow-hidden rounded-2xl bg-white shadow-[0_1px_24px_rgba(46,36,48,0.07)] ring-1 ring-petal" data-id="element-324" data-mp="3q4ukweli1ulh">
        {results.map(item => {
        const isOpen = openIndex === item.index;
        const panelId = `${idPrefix}-panel-${lang}-${item.index}`;
        const buttonId = `${idPrefix}-button-${lang}-${item.index}`;
        return <li key={`${lang}-${item.index}`} data-id="element-325" data-mp="dn7acabhadd8">
              <h3 data-id="element-326" data-mp="doinh0qtcxh0">
                <button type="button" id={buttonId} aria-expanded={isOpen} aria-controls={panelId} onClick={() => setOpenIndex(isOpen ? null : item.index)} className={`flex w-full items-start gap-4 p-4 text-left transition-colors duration-150 ease-smooth sm:px-6 sm:py-5 ${isOpen ? 'bg-petal/50' : 'hover:bg-petal/40'}`} data-id="element-327" data-mp="dq38nzr8n427">
                  <HelpCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-mauve/70" aria-hidden="true" data-id="element-328" data-mp="13zu81pt6zk8x" />
                  <span className="flex-1 text-base font-semibold leading-snug text-mauve" data-id="element-329" data-mp="18yx3qjna8e48">
                    {item.q}
                  </span>
                  <span aria-hidden="true" className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-champagne/60 text-champagne" data-id="element-330" data-mp="1hxr3givwtkm0">
                    <PlusIcon className={`h-4 w-4 transition-transform duration-200 ease-smooth ${isOpen ? 'rotate-45' : ''}`} data-id="element-331" data-mp="1rudmd24oe1le" />
                  </span>
                </button>
              </h3>
              <AnimatePresence initial={false} data-id="element-332" data-mp="25o4mjjwio94g">
                {isOpen ? <motion.div key={panelId} id={panelId} role="region" aria-labelledby={buttonId} initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.22,
              ease: [0.23, 1, 0.32, 1]
            }} className="overflow-hidden bg-petal/60" data-id="element-333" data-mp="2aln1bqx7tikd">
                    <p className="px-4 pb-5 pl-[3.25rem] pt-1 text-sm leading-relaxed text-[#333] sm:px-6 sm:pl-[3.75rem]" data-id="element-334" data-mp="1dztjeyyitn3q">
                      {item.a}
                    </p>
                  </motion.div> : null}
              </AnimatePresence>
            </li>;
      })}
        {results.length === 0 ? <li className="p-8 text-center text-sm text-ink/60" data-id="element-335" data-mp="1iy3i3jjgf5u3">{t.faq.noResults}</li> : null}
      </ul>
    </div>;
}